import React, { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, ListItemIcon, ListItemText, Drawer, Popover, List, ListItem, ListItemButton, IconButton, Typography, Paper, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { activeNavigationItem, navigationItems, visibleNavigationCount } from './navigationItems';
import { runtime } from '../../platform/runtime';
import CallDeviceAction from './CallDeviceAction';
import { isSmartConnectUnit } from '../../utils/smartConnect';

// The non-interactive measuring copies use exactly the same dimensions as the
// visible controls. Selection never changes font size or intrinsic width.
const itemSx = {
    boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', gap: '4px',
    minWidth: 64, minHeight: 70, px: 1.5, py: 1,
    fontFamily: 'inherit', fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.25,
    whiteSpace: 'nowrap', textAlign: 'center', textDecoration: 'none',
    '& .MuiSvgIcon-root': { fontSize: '1.5rem' }
};

function Navigation({ bottom, unit, showCaregiverPages }) {
    const customerKey = unit?.CustomerKey;
    const { pathname } = useLocation();
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const items = useMemo(() => navigationItems(customerKey, showCaregiverPages), [customerKey, showCaregiverPages]);
    const primary = useMemo(() => items.filter(item => !item.moreOnly), [items]);
    const [visibleCount, setVisibleCount] = useState(primary.length);
    const [drawerContext, setDrawerContext] = useState(null);
    const paperRef = useRef(null), rowRef = useRef(null), measureRef = useRef(null), moreRef = useRef(null);
    const drawerId = useId();
    const closeRef = useRef(null);
    const visible = primary.slice(0, visibleCount);
    const overflow = items.filter(item => !visible.includes(item));
    const activeItem = activeNavigationItem(items, pathname);
    const overflowActive = overflow.some(item => item.id === activeItem?.id);
    const context = `${pathname}:${overflow.map(item => item.id).join(',')}`;
    const open = drawerContext === context && overflow.length > 0;

    useLayoutEffect(() => {
        if (open) closeRef.current?.focus();
    }, [open]);

    useLayoutEffect(() => {
        if (drawerContext !== null && drawerContext !== context) setDrawerContext(null);
    }, [context, drawerContext]);

    useLayoutEffect(() => {
        let disposed = false;
        let frame;
        const measure = () => {
            if (disposed) return;
            const available = rowRef.current.getBoundingClientRect().width;
            const widths = [...measureRef.current.children].map(node => Math.ceil(node.getBoundingClientRect().width));
            if (available > 0 && widths.every(width => width > 0)) {
                const count = visibleNavigationCount(widths.slice(0, -1), widths.at(-1), Math.floor(available), items.some(item => item.moreOnly));
                const focusedId = document.activeElement?.getAttribute('data-navigation-item');
                if (primary.slice(count).some(item => item.id === focusedId)) {
                    // Preserve keyboard focus when a destination moves into More.
                    cancelAnimationFrame(frame);
                    frame = requestAnimationFrame(() => moreRef.current?.focus());
                }
                setVisibleCount(count);
            }
            if (bottom) {
                const height = paperRef.current.getBoundingClientRect().height;
                if (height > 0) document.documentElement.style.setProperty('--app-bottom-nav-height', `${height}px`);
            }
        };
        const observer = new ResizeObserver(measure);
        observer.observe(rowRef.current);
        observer.observe(paperRef.current);
        for (const node of measureRef.current.children) observer.observe(node);
        window.addEventListener('resize', measure);
        document.fonts?.addEventListener('loadingdone', measure);
        document.fonts?.ready.then(measure);
        measure();
        return () => {
            disposed = true;
            cancelAnimationFrame(frame);
            observer.disconnect();
            window.removeEventListener('resize', measure);
            document.fonts?.removeEventListener('loadingdone', measure);
            if (bottom) document.documentElement.style.removeProperty('--app-bottom-nav-height');
        };
    }, [bottom, items, primary]);

    const controlSx = selected => ({
        ...itemSx, flex: '1 0 auto', color: selected ? (bottom ? 'primary.main' : 'inherit') : (bottom ? 'text.secondary' : 'inherit'),
        borderRadius: 1,
        '&::after': {
            content: '""', position: 'absolute', bottom: 2, left: '25%', right: '25%',
            height: 3, borderRadius: 2, bgcolor: selected ? 'currentColor' : 'transparent'
        },
        '&:hover': { bgcolor: bottom ? 'action.hover' : 'rgba(255,255,255,0.12)' },
        '&.Mui-disabled': { opacity: 0.4 },
        '&.Mui-focusVisible': { outline: '2px solid currentColor', outlineOffset: -3 }
    });
    const contents = ({ Icon, label }) => <><Icon /><span>{label}</span></>;
    const OverflowPanel = bottom ? Drawer : Popover;
    const overflowPanelProps = bottom ? {
        anchor: 'right',
        ModalProps: { BackdropProps: { sx: { bgcolor: 'rgba(0, 0, 0, 0.5)' } } }
    } : {
        anchorEl: moreRef.current,
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        transformOrigin: { vertical: 'top', horizontal: 'right' },
        marginThreshold: 16
    };

    return (
        <Paper ref={paperRef} component="nav" aria-label="Main navigation" elevation={bottom ? 8 : 0}
            sx={bottom ? {
                position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000, borderRadius: 0,
                bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider',
                pb: 'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))',
                pl: 'var(--safe-area-inset-left, env(safe-area-inset-left, 0px))',
                pr: 'var(--safe-area-inset-right, env(safe-area-inset-right, 0px))'
            } : { position: 'relative', maxWidth: 800, mx: 'auto', mb: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <Box ref={rowRef} sx={{ display: 'flex', maxWidth: 800, mx: 'auto', minWidth: 0 }}>
                {visible.map(item => <ButtonBase key={item.id} data-navigation-item={item.id}
                    component={item.disabled ? 'button' : Link} to={item.disabled ? undefined : item.path}
                    disabled={item.disabled} aria-current={activeItem?.id === item.id ? 'page' : undefined}
                    sx={controlSx(activeItem?.id === item.id)}>{contents(item)}</ButtonBase>)}
                {overflow.length > 0 && <ButtonBase ref={moreRef} onClick={() => setDrawerContext(open ? null : context)}
                    aria-label={overflowActive ? `More, current page: ${activeItem.label}` : 'More'}
                    aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? drawerId : undefined}
                    sx={{ ...controlSx(overflowActive), minWidth: 48, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                    <MoreHorizIcon /><span>More</span>
                </ButtonBase>}
            </Box>
            <Box aria-hidden="true" sx={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', visibility: 'hidden', pointerEvents: 'none' }}>
                <Box ref={measureRef} sx={{ display: 'flex', width: 'max-content' }}>
                    {[...primary, { id: 'more', label: 'More', Icon: MoreHorizIcon }].map(item =>
                        <Box key={item.id} sx={{ ...itemSx, flexShrink: 0 }}>{contents(item)}</Box>)}
                </Box>
            </Box>
            <OverflowPanel {...overflowPanelProps} transitionDuration={reducedMotion ? 0 : undefined} open={open} onClose={() => setDrawerContext(null)}
                PaperProps={{ id: drawerId, role: 'dialog', 'aria-modal': true, 'aria-label': 'More pages', tabIndex: -1,
                    sx: bottom ? { width: 320, maxWidth: 'calc(100vw - 48px)', boxSizing: 'border-box',
                        borderTopLeftRadius: 20, border: 1, borderColor: 'divider', boxShadow: 24,
                        height: 'auto', top: 'auto',
                        bottom: 'var(--app-bottom-nav-height, 0px)',
                        minHeight: 'min(50dvh, calc(100dvh - var(--app-bottom-nav-height, 0px)))',
                        maxHeight: 'calc(100dvh - var(--app-bottom-nav-height, 0px) - var(--safe-area-inset-top, env(safe-area-inset-top, 0px)))',
                        pb: 0, pr: 'var(--safe-area-inset-right, env(safe-area-inset-right, 0px))'
                    } : {
                        width: 320, maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100dvh - 32px)',
                        boxSizing: 'border-box', borderRadius: 3, border: 1, borderColor: 'divider', boxShadow: 8
                    } }}>
                <Box component="header" sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 1, px: 2, py: 1,
                    bgcolor: 'primary.main', color: 'primary.contrastText', borderBottom: 1, borderColor: 'divider' }}>
                    <Typography component="h2" variant="h6" sx={{ flex: 1, fontWeight: 700, overflowWrap: 'anywhere' }}>More</Typography>
                    <IconButton ref={closeRef} autoFocus aria-label="Close More" onClick={() => setDrawerContext(null)}
                        sx={{ minWidth: 48, minHeight: 48, color: 'inherit', bgcolor: 'rgba(255,255,255,0.12)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
                            '&.Mui-focusVisible': { outline: '2px solid currentColor', outlineOffset: 2 } }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <List aria-label="Additional pages" sx={{ px: 1.5, py: bottom ? 2 : 1.5, display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
                    {overflow.map(item => <ListItem key={item.id} disablePadding><ListItemButton component={item.disabled ? 'button' : Link}
                        to={item.disabled ? undefined : item.path} disabled={item.disabled}
                        selected={item.id === activeItem?.id} aria-current={item.id === activeItem?.id ? 'page' : undefined}
                        onClick={() => setDrawerContext(null)} sx={{ width: '100%', minHeight: bottom ? 60 : 48, borderRadius: 2, border: 1,
                            borderColor: item.id === activeItem?.id ? 'primary.main' : 'divider',
                            whiteSpace: 'normal', overflowWrap: 'anywhere', px: 1.5,
                            '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 } }}>
                        <ListItemIcon sx={{ minWidth: '36px !important', color: 'primary.main', '& .MuiSvgIcon-root': { fontSize: 24 } }}><item.Icon /></ListItemIcon>
                        <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600 }} sx={{ minWidth: 0 }} />
                    </ListItemButton></ListItem>)}
                    {open && runtime.isNative() && <CallDeviceAction unit={unit} onClose={() => setDrawerContext(null)} />}
                </List>
            </OverflowPanel>
        </Paper>
    );
}

export default function AdaptiveNavigation({ bottom = false, showCaregiverPages = true }) {
    const unit = useSelector(state => state.auth.activeUnit);
    const userId = useSelector(state => state.auth.user?.user ?? state.auth.user?.UserKey);
    const token = useSelector(state => state.auth.token);
    // Close overflow immediately when identity/permissions change. No stale
    // customer destination may remain actionable in a previously opened drawer.
    const scope = JSON.stringify([userId, token, unit?.CustomerKey, unit?.UnitSerNum, showCaregiverPages, isSmartConnectUnit(unit)]);
    return <Navigation key={scope} bottom={bottom} unit={unit} showCaregiverPages={showCaregiverPages} />;
}
